/**
 * 国际化工具类
 * 通过URL参数 &lan=en 加载不同语言包
 */
import zhCN from './zh-CN'
import en from './en'
import ru from './ru'
import fr from './fr'
import es from './es'
import ar from './ar'
import pt from './pt'

// 语言包映射（兼容连字符-和下划线_两种格式）
const locales = {
  'zh-CN': zhCN,
  'zh': zhCN,
  'zh_CN': zhCN, //简体中文(中国)
  'en': en,
  'en-US': en,
  'en_US': en, //英语(美国)
  'ru': ru,
  'ru-RU': ru, //俄语(俄罗斯)
  'ru_RU': ru, //俄语(俄罗斯)
  'fr': fr,
  'fr-FR': fr, //法语(法国)
  'fr_FR': fr, //法语(法国)
  'es': es,
  'es-ES': es, //西班牙语(西班牙)
  'es_ES': es, //西班牙语(西班牙)
  'ar': ar,
  'ar-SA': ar, //阿拉伯语(沙特阿拉伯)
  'ar_SA': ar, //阿拉伯语(沙特阿拉伯)
  'pt': pt,
  'pt-PT': pt, //葡萄牙语(葡萄牙)
  'pt_PT': pt //葡萄牙语(葡萄牙)
}

// 默认语言
let currentLocale = 'zh-CN'

// 当前语言包
let currentMessages = zhCN

/**
 * 初始化语言设置
 * @param {string} lang - 语言代码，如 'en', 'zh-CN'
 */
export function initLocale(lang) {
  if (lang && locales[lang]) {
    currentLocale = lang
    currentMessages = locales[lang]
  } else {
    // 尝试匹配语言前缀（兼容连字符-和下划线_格式）
    const prefix = lang ? lang.split(/[-_]/)[0] : 'zh'
    const matchedKey = Object.keys(locales).find(k => k.startsWith(prefix))
    if (matchedKey) {
      currentLocale = matchedKey
      currentMessages = locales[matchedKey]
    }
  }
}

/**
 * 从URL参数初始化语言
 * @param {string} urlParam - URL中的语言参数，如 ?lan=en
 */
export function initLocaleFromUrl(urlParam) {
  if (urlParam) {
    initLocale(urlParam)
  }
}

/**
 * 获取当前语言
 */
export function getCurrentLocale() {
  return currentLocale
}

/**
 * 翻译函数
 * @param {string} text - 需要翻译的中文文本
 * @param {string} category - 分类（可选），如 'part', 'defect' 等
 * @returns {string} 翻译后的文本
 */
export function t(text, category = null) {
  if (!text) return ''
  
  // 如果当前是中文，直接返回原文
  if (currentLocale.startsWith('zh')) {
    return text
  }
  
  // 如果指定了分类，先在分类中查找
  if (category && currentMessages[category] && currentMessages[category][text]) {
    return currentMessages[category][text]
  }
  
  // 遍历所有分类查找
  for (const cat of Object.keys(currentMessages)) {
    if (currentMessages[cat][text]) {
      return currentMessages[cat][text]
    }
  }
  
  // 未找到翻译，返回原文
  return text
}

/**
 * 翻译检测部位名称
 */
export function tPart(name) {
  return t(name, 'part')
}

/**
 * 翻译缺陷名称
 * 支持三种格式：
 * 1. 单个字符串：'划伤' -> 'Scratch'
 * 2. 数组格式：['划伤', '钣金修复'] -> ['Scratch', 'Sheet Metal Repair']
 * 3. 用"、"分隔的字符串：'划伤、喷漆' -> 'Scratch、Repaint'
 */
export function tDefect(name) {
  if (!name) return name
  
  // 如果是数组，遍历翻译每个元素
  if (Array.isArray(name)) {
    return name.map(item => t(item, 'defect') || item)
  }
  
  // 如果是字符串，检查是否包含"、"分隔符
  if (typeof name === 'string') {
    if (name.includes('、')) {
      // 按"、"拆分，翻译后用"、"连接
      const parts = name.split('、')
      const translatedParts = parts.map(part => t(part.trim(), 'defect') || part.trim())
      return translatedParts.join('、')
    }
    // 普通字符串，直接翻译
    return t(name, 'defect')
  }
  
  return name
}

/**
 * 翻译检测项名称
 */
export function tInspectionItem(name) {
  return t(name, 'inspectionItem')
}

/**
 * 翻译损伤等级
 */
export function tDamageLevel(name) {
  return t(name, 'damageLevel')
}

/**
 * 翻译评分说明
 */
export function tScoreRemark(text) {
  return t(text, 'scoreRemark')
}


/**
 * 翻译等级（如 A级 -> Grade A）
 */
export function tGrade(grade) {
  return t(grade, 'grade')
}

/**
 * 翻译页面标题
 */
export function tTitle(text) {
  return t(text, 'title')
}

/**
 * 翻译提示文案
 */
export function tTips(text) {
  return t(text, 'tips')
}

/**
 * 翻译评分规则标题
 */
export function tRuleTitle(text) {
  return t(text, 'ruleTitle')
}

/**
 * 翻译外观评分规则
 */
export function tExteriorRule(text) {
  return t(text, 'exteriorRule')
}

/**
 * 翻译内饰评分规则
 */
export function tInteriorRule(text) {
  return t(text, 'interiorRule')
}

/**
 * 翻译骨架评级规则
 */
export function tSkeletonRule(text) {
  return t(text, 'skeletonRule')
}

/**
 * 翻译综合评级规则
 */
export function tOverallRule(text) {
  return t(text, 'overallRule')
}

// Vue 插件形式
export const i18nPlugin = {
  install(Vue) {
    Vue.prototype.$t = t
    Vue.prototype.$tPart = tPart
    Vue.prototype.$tDefect = tDefect
    Vue.prototype.$tInspectionItem = tInspectionItem
    Vue.prototype.$tDamageLevel = tDamageLevel
    Vue.prototype.$tScoreRemark = tScoreRemark
    Vue.prototype.$tGrade = tGrade
    Vue.prototype.$tTitle = tTitle
    Vue.prototype.$tTips = tTips
    Vue.prototype.$tRuleTitle = tRuleTitle
    Vue.prototype.$tExteriorRule = tExteriorRule
    Vue.prototype.$tInteriorRule = tInteriorRule
    Vue.prototype.$tSkeletonRule = tSkeletonRule
    Vue.prototype.$tOverallRule = tOverallRule
  }
}

export default {
  initLocale,
  initLocaleFromUrl,
  getCurrentLocale,
  t,
  tPart,
  tDefect,
  tInspectionItem,
  tDamageLevel,
  tScoreRemark,
  tGrade,
  tTitle,
  tTips,
  tRuleTitle,
  tExteriorRule,
  tInteriorRule,
  tSkeletonRule,
  tOverallRule,
  i18nPlugin
}